import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Building2, AlertTriangle, FileText, ClipboardCheck, Shield,
  TrendingUp, TrendingDown, Calendar, Filter, RefreshCw,
  ChevronRight, BarChart3, PieChart, Activity, CheckCircle,
  XCircle, Clock, Download, Eye, Plus, ArrowUpDown, Users,
  Target, AlertCircle, Repeat, ArrowLeft
} from 'lucide-react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

const ParentGCDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState(null);
  
  // Filter state
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedSubcontractor, setSelectedSubcontractor] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Drill-down data
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownType, setDrilldownType] = useState(null);
  
  // Comparison data (Stage 4)
  const [projectComparison, setProjectComparison] = useState(null);
  const [contractorComparison, setContractorComparison] = useState(null);
  
  // Corrective Actions (Stage 5)
  const [showCreateCADialog, setShowCreateCADialog] = useState(false);
  const [caForm, setCaForm] = useState({
    projectId: '',
    contractorId: '',
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    category: ''
  });
  const [correctiveActions, setCorrectiveActions] = useState([]);
  
  // Projects Management (NEW)
  const [projects, setProjects] = useState([]);
  const [availableProjectGcs, setAvailableProjectGcs] = useState([]);
  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [showProjectDetailsDialog, setShowProjectDetailsDialog] = useState(false);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    location: '',
    startDate: '',
    endDate: '',
    projectGcId: '',
    requirements: {
      dailyJsaRequired: false,
      weeklyToolboxTalkRequired: false,
      preTaskPlanRequired: false,
      auditFrequency: 'monthly'
    }
  });
  const [editingProject, setEditingProject] = useState(null);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load dashboard data (Stage 2)
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/parent-gc/dashboard');
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Parent GC access required');
        navigate('/');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Load filter options
  const loadFilters = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent-gc/filters');
      setFilters(response.data);
    } catch (error) {
      console.error('Failed to load filters:', error);
    }
  }, []);

  // Load drill-down data (Stage 3)
  const loadDrilldown = useCallback(async (type) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      if (selectedSubcontractor !== 'all') params.append('subcontractor_id', selectedSubcontractor);
      if (selectedSeverity !== 'all') params.append('severity', selectedSeverity);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (selectedPriority !== 'all') params.append('priority', selectedPriority);
      if (sortBy) params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const response = await axiosInstance.get(`/parent-gc/drilldown/${type}?${params.toString()}`);
      setDrilldownData(response.data);
      setDrilldownType(type);
    } catch (error) {
      toast.error(`Failed to load ${type} data`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedProject, selectedSubcontractor, selectedSeverity, selectedStatus, selectedPriority, sortBy, sortOrder]);

  // Load comparison data (Stage 4)
  const loadComparisons = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const [projectRes, contractorRes] = await Promise.all([
        axiosInstance.get(`/parent-gc/comparison/projects?${params.toString()}`),
        axiosInstance.get(`/parent-gc/comparison/contractors?${params.toString()}`)
      ]);
      
      setProjectComparison(projectRes.data);
      setContractorComparison(contractorRes.data);
    } catch (error) {
      console.error('Failed to load comparisons:', error);
    }
  }, [startDate, endDate]);

  // Load corrective actions (Stage 5)
  const loadCorrectiveActions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      if (selectedSubcontractor !== 'all') params.append('contractor_id', selectedSubcontractor);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (selectedPriority !== 'all') params.append('priority', selectedPriority);
      
      const response = await axiosInstance.get(`/parent-gc/corrective-actions?${params.toString()}`);
      setCorrectiveActions(response.data.correctiveActions || []);
    } catch (error) {
      console.error('Failed to load corrective actions:', error);
    }
  }, [selectedProject, selectedSubcontractor, selectedStatus, selectedPriority]);

  // Create corrective action (Stage 5)
  const handleCreateCA = async () => {
    if (!caForm.title || !caForm.description) {
      toast.error('Title and description are required');
      return;
    }
    
    try {
      await axiosInstance.post('/parent-gc/corrective-actions', caForm);
      toast.success('Corrective action created');
      setShowCreateCADialog(false);
      setCaForm({
        projectId: '',
        contractorId: '',
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        category: ''
      });
      loadCorrectiveActions();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create corrective action');
    }
  };

  // Update corrective action status
  const handleUpdateCAStatus = async (actionId, newStatus) => {
    try {
      await axiosInstance.patch(`/parent-gc/corrective-actions/${actionId}`, { status: newStatus });
      toast.success('Status updated');
      loadCorrectiveActions();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ========== PROJECT MANAGEMENT FUNCTIONS ==========
  
  // Load projects owned by Parent GC
  const loadProjects = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent-gc/projects');
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  }, []);

  // Load available Project GCs
  const loadAvailableProjectGcs = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/parent-gc/available-project-gcs');
      setAvailableProjectGcs(response.data.companies || []);
    } catch (error) {
      console.error('Failed to load available Project GCs:', error);
    }
  }, []);

  // Create new project
  const handleCreateProject = async () => {
    if (!projectForm.name || !projectForm.projectGcId) {
      toast.error('Project name and Project GC are required');
      return;
    }
    
    try {
      await axiosInstance.post('/parent-gc/projects', projectForm);
      toast.success('Project created successfully');
      setShowCreateProjectDialog(false);
      setProjectForm({
        name: '',
        description: '',
        location: '',
        startDate: '',
        endDate: '',
        projectGcId: '',
        requirements: {
          dailyJsaRequired: false,
          weeklyToolboxTalkRequired: false,
          preTaskPlanRequired: false,
          auditFrequency: 'monthly'
        }
      });
      loadProjects();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  // Update existing project
  const handleUpdateProject = async () => {
    if (!editingProject) return;
    
    try {
      await axiosInstance.put(`/parent-gc/projects/${editingProject.id}`, projectForm);
      toast.success('Project updated successfully');
      setShowEditProjectDialog(false);
      setEditingProject(null);
      loadProjects();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };

  // Delete/archive project
  const handleDeleteProject = async (projectId) => {
    if (!confirm('Are you sure you want to archive this project?')) return;
    
    try {
      await axiosInstance.delete(`/parent-gc/projects/${projectId}`);
      toast.success('Project archived');
      loadProjects();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive project');
    }
  };

  // View project details
  const handleViewProjectDetails = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/parent-gc/projects/${projectId}`);
      setSelectedProjectDetails(response.data.project);
      setShowProjectDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };

  // Open edit dialog
  const openEditProjectDialog = (project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name || '',
      description: project.description || '',
      location: project.location || '',
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      projectGcId: project.gcCompanyId || '',
      status: project.status || 'active',
      requirements: project.requirements || {
        dailyJsaRequired: false,
        weeklyToolboxTalkRequired: false,
        preTaskPlanRequired: false,
        auditFrequency: 'monthly'
      }
    });
    setShowEditProjectDialog(true);
  };

  // Export CSV (Stage 6)
  const handleExport = async (dataType) => {
    try {
      const params = new URLSearchParams();
      params.append('data_type', dataType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      
      const response = await axiosInstance.get(`/parent-gc/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `parent_gc_${dataType}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${dataType} exported`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Export summary (Stage 6)
  const handleExportSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axiosInstance.get(`/parent-gc/export/summary?${params.toString()}`);
      
      // Download as JSON (can be converted to PDF on frontend or backend)
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `portfolio_summary_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Summary exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  useEffect(() => {
    loadDashboard();
    loadFilters();
  }, [loadDashboard, loadFilters]);

  useEffect(() => {
    if (activeTab === 'comparison') {
      loadComparisons();
    } else if (activeTab === 'actions') {
      loadCorrectiveActions();
    } else if (activeTab === 'incidents') {
      loadDrilldown('incidents');
    } else if (activeTab === 'audits') {
      loadDrilldown('audits');
    } else if (activeTab === 'jsas') {
      loadDrilldown('jsas');
    } else if (activeTab === 'projects') {
      loadProjects();
      loadAvailableProjectGcs();
    }
  }, [activeTab, loadComparisons, loadCorrectiveActions, loadDrilldown, loadProjects, loadAvailableProjectGcs]);

  const handleRefresh = () => {
    loadDashboard();
    loadFilters();
    if (activeTab === 'comparison') loadComparisons();
    if (activeTab === 'actions') loadCorrectiveActions();
    if (activeTab === 'projects') { loadProjects(); loadAvailableProjectGcs(); }
    if (['incidents', 'audits', 'jsas'].includes(activeTab)) loadDrilldown(activeTab);
    toast.success('Data refreshed');
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'pending':
      case 'reported': return 'bg-yellow-100 text-yellow-800';
      case 'closed':
      case 'resolved':
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">Loading Parent GC Portfolio...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-gray-600">Failed to load dashboard. Please try again.</p>
          <Button onClick={loadDashboard} className="mt-4">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="w-8 h-8 text-purple-600" />
              {dashboard.parentGcName} - Portfolio Analytics
            </h1>
            <p className="text-gray-600 mt-1">
              Enterprise view across {dashboard.childGcCount} child GC companies • {dashboard.totalProjects} projects
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleExportSummary}>
              <Download className="w-4 h-4 mr-2" />
              Export Summary
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="parent-gc-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </div>
        </div>
      </div>

      {/* Stage 2: KPI Tiles - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-red-500"
          onClick={() => { setActiveTab('incidents'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Total Incidents</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.kpis?.totalIncidents || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Last 30 days</p>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
          onClick={() => { setActiveTab('actions'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Open Actions</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.kpis?.openCorrectiveActions || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-orange-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Corrective actions</p>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-yellow-500"
          onClick={() => { setActiveTab('audits'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Audit Fail Rate</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboard.kpis?.auditFailureRate || 0}%</p>
              </div>
              <ClipboardCheck className="w-8 h-8 text-yellow-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Score &lt; 70%</p>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500"
          onClick={() => { setActiveTab('comparison'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Active Projects</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.kpis?.activeProjects || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">In progress</p>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500"
          onClick={() => { setActiveTab('comparison'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Subcontractors</p>
                <p className="text-2xl font-bold text-purple-600">{dashboard.kpis?.subcontractorsUnderOversight || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Under oversight</p>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-pink-500"
          onClick={() => { setActiveTab('audits'); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Repeat Findings</p>
                <p className="text-2xl font-bold text-pink-600">{dashboard.kpis?.repeatFindings || 0}</p>
              </div>
              <Repeat className="w-8 h-8 text-pink-200" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Recurring issues</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Project</label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.projects?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Subcontractor</label>
              <Select value={selectedSubcontractor} onValueChange={setSelectedSubcontractor}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.subcontractors?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.statuses?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Priority</label>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.priorities?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="audits">Audits</TabsTrigger>
          <TabsTrigger value="jsas">JSAs</TabsTrigger>
          <TabsTrigger value="comparison">Comparison</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          {/* Child GCs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Child GC Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboard.childGcs?.map((gc) => (
                  <div key={gc.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">{gc.name}</p>
                        <p className="text-sm text-gray-500">{gc.companyCode}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {(!dashboard.childGcs || dashboard.childGcs.length === 0) && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    No child GC companies linked
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Projects Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Projects Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">{dashboard.totalProjects}</p>
                  <p className="text-sm text-gray-600">Total</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{dashboard.activeProjects}</p>
                  <p className="text-sm text-gray-600">Active</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-3xl font-bold text-gray-600">{dashboard.completedProjects}</p>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROJECTS TAB - Project Management */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  My Projects ({projects.length})
                </CardTitle>
                <Button onClick={() => { loadAvailableProjectGcs(); setShowCreateProjectDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Button>
              </div>
              <CardDescription>
                Projects you have created and assigned to Project GCs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Project GC</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Subcontractors</TableHead>
                      <TableHead>Recent Activity</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{project.name}</p>
                            <p className="text-xs text-gray-500">{project.description?.substring(0, 50)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-purple-50">
                            {project.gcCompanyName || 'Not Assigned'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">{project.location || '-'}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(project.status)}>
                            {project.status || 'active'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span>{project.subcontractorCount || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertTriangle className="w-3 h-3" />
                              {project.recentIncidents || 0} incidents
                            </div>
                            <div className="flex items-center gap-1 text-blue-600">
                              <ClipboardCheck className="w-3 h-3" />
                              {project.recentAudits || 0} audits
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleViewProjectDetails(project.id)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditProjectDialog(project)}>
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteProject(project.id)}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">No projects created yet</p>
                  <Button onClick={() => { loadAvailableProjectGcs(); setShowCreateProjectDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Project
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INCIDENTS TAB - Stage 3 */}
        <TabsContent value="incidents">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Incidents ({drilldownData?.total || 0})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('incidents')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {drilldownData?.data?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="cursor-pointer" onClick={() => { setSortBy('dateTime'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                        Date <ArrowUpDown className="w-3 h-3 inline ml-1" />
                      </TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Subcontractor</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Location</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drilldownData.data.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="whitespace-nowrap">
                          {item.dateTime ? new Date(item.dateTime).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="max-w-32 truncate">{item.projectName}</TableCell>
                        <TableCell className="max-w-32 truncate">{item.subcontractorName}</TableCell>
                        <TableCell><Badge variant="outline">{item.type}</Badge></TableCell>
                        <TableCell><Badge className={getSeverityColor(item.severity)}>{item.severity}</Badge></TableCell>
                        <TableCell><Badge className={getStatusColor(item.status)}>{item.status}</Badge></TableCell>
                        <TableCell className="max-w-32 truncate">{item.location}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">No incidents found</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUDITS TAB - Stage 3 */}
        <TabsContent value="audits">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-500" />
                  Audits ({drilldownData?.total || 0})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('audits')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {drilldownData?.data?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Subcontractor</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead className="text-right">Issues</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drilldownData.data.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.date ? new Date(item.date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="max-w-32 truncate">{item.projectName}</TableCell>
                        <TableCell className="max-w-32 truncate">{item.subcontractorName}</TableCell>
                        <TableCell className="max-w-40 truncate">{item.title}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold ${item.overallScore >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.overallScore}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{item.issuesCount}</TableCell>
                        <TableCell><Badge className={getStatusColor(item.status)}>{item.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">No audits found</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* JSAs TAB - Stage 3 */}
        <TabsContent value="jsas">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-500" />
                  JSAs ({drilldownData?.total || 0})
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => handleExport('jsas')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {drilldownData?.data?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Subcontractor</TableHead>
                      <TableHead>Task Name</TableHead>
                      <TableHead className="text-right">Hazards</TableHead>
                      <TableHead className="text-right">Signatures</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drilldownData.data.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="max-w-32 truncate">{item.projectName}</TableCell>
                        <TableCell className="max-w-32 truncate">{item.subcontractorName}</TableCell>
                        <TableCell className="max-w-40 truncate">{item.taskName}</TableCell>
                        <TableCell className="text-right">{item.hazardCount}</TableCell>
                        <TableCell className="text-right">{item.signatureCount}</TableCell>
                        <TableCell><Badge className={getStatusColor(item.status)}>{item.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">No JSAs found</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPARISON TAB - Stage 4 */}
        <TabsContent value="comparison" className="space-y-6">
          {/* Project Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Project Comparison
              </CardTitle>
              <CardDescription>Compare safety metrics across projects</CardDescription>
            </CardHeader>
            <CardContent>
              {projectComparison?.projects?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project</TableHead>
                      <TableHead>GC Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Incidents</TableHead>
                      <TableHead className="text-right">Audit Fail %</TableHead>
                      <TableHead className="text-right">Open CAs</TableHead>
                      <TableHead className="text-right">Repeat Findings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projectComparison.projects.map((p) => (
                      <TableRow key={p.projectId}>
                        <TableCell className="font-medium">{p.projectName}</TableCell>
                        <TableCell>{p.gcCompanyName}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={p.status === 'active' ? 'bg-green-50' : ''}>
                            {p.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={p.incidentCount > 0 ? 'text-red-600 font-semibold' : ''}>
                            {p.incidentCount}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={p.auditFailRate > 30 ? 'text-red-600' : 'text-green-600'}>
                            {p.auditFailRate}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{p.openCorrectiveActions}</TableCell>
                        <TableCell className="text-right">{p.repeatFindings}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">No projects to compare</div>
              )}
            </CardContent>
          </Card>

          {/* Contractor Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Contractor Performance
              </CardTitle>
              <CardDescription>Compare contractor safety performance</CardDescription>
            </CardHeader>
            <CardContent>
              {contractorComparison?.contractors?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contractor</TableHead>
                      <TableHead className="text-right">Projects</TableHead>
                      <TableHead className="text-right">Incidents</TableHead>
                      <TableHead className="text-right">Audit Findings</TableHead>
                      <TableHead className="text-right">Avg Close-Out (days)</TableHead>
                      <TableHead className="text-right">Repeat Issues</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractorComparison.contractors.map((c) => (
                      <TableRow key={c.contractorId}>
                        <TableCell className="font-medium">{c.contractorName}</TableCell>
                        <TableCell className="text-right">{c.projectCount}</TableCell>
                        <TableCell className="text-right">
                          <span className={c.incidentCount > 0 ? 'text-red-600 font-semibold' : ''}>
                            {c.incidentCount}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{c.auditFindings}</TableCell>
                        <TableCell className="text-right">{c.avgCloseOutDays}</TableCell>
                        <TableCell className="text-right">{c.repeatIssues}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">No contractors to compare</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CORRECTIVE ACTIONS TAB - Stage 5 */}
        <TabsContent value="actions">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-500" />
                    Corrective Actions ({correctiveActions.length})
                  </CardTitle>
                  <CardDescription>Create and manage corrective actions (no training)</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExport('corrective_actions')}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Button size="sm" onClick={() => setShowCreateCADialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Action
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {correctiveActions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Created</TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {correctiveActions.map((ca) => (
                      <TableRow key={ca.id}>
                        <TableCell>{ca.createdAt ? new Date(ca.createdAt).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="max-w-48 truncate font-medium">{ca.title}</TableCell>
                        <TableCell><Badge className={getPriorityColor(ca.priority)}>{ca.priority}</Badge></TableCell>
                        <TableCell><Badge className={getStatusColor(ca.status)}>{ca.status}</Badge></TableCell>
                        <TableCell>{ca.dueDate ? new Date(ca.dueDate).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="text-right">
                          <Select 
                            value={ca.status}
                            onValueChange={(val) => handleUpdateCAStatus(ca.id, val)}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No corrective actions yet. Click Create Action to add one.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Corrective Action Dialog - Stage 5 */}
      <Dialog open={showCreateCADialog} onOpenChange={setShowCreateCADialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Corrective Action</DialogTitle>
            <DialogDescription>
              Assign to a project or contractor. Note: Training cannot be assigned.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={caForm.title}
                onChange={(e) => setCaForm({ ...caForm, title: e.target.value })}
                placeholder="Brief description of required action"
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={caForm.description}
                onChange={(e) => setCaForm({ ...caForm, description: e.target.value })}
                placeholder="Detailed description of the corrective action required"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project</Label>
                <Select value={caForm.projectId} onValueChange={(val) => setCaForm({ ...caForm, projectId: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filters?.projects?.filter(p => p.id !== 'all').map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Contractor</Label>
                <Select value={caForm.contractorId} onValueChange={(val) => setCaForm({ ...caForm, contractorId: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filters?.subcontractors?.filter(s => s.id !== 'all').map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={caForm.priority} onValueChange={(val) => setCaForm({ ...caForm, priority: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={caForm.dueDate}
                  onChange={(e) => setCaForm({ ...caForm, dueDate: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Category (optional)</Label>
              <Input
                value={caForm.category}
                onChange={(e) => setCaForm({ ...caForm, category: e.target.value })}
                placeholder="e.g., PPE, Fall Protection, Housekeeping"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCADialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCA}>
              <Plus className="w-4 h-4 mr-2" />
              Create Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={showCreateProjectDialog} onOpenChange={setShowCreateProjectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Create a project and assign it to a Project GC for management.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={projectForm.name}
                onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                placeholder="Enter project name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-gc">Assign Project GC *</Label>
              <Select
                value={projectForm.projectGcId}
                onValueChange={(value) => setProjectForm({...projectForm, projectGcId: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a Project GC" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjectGcs.map((gc) => (
                    <SelectItem key={gc.id} value={gc.id}>
                      {gc.name} {gc.companyCode ? `(${gc.companyCode})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">The Project GC will manage day-to-day operations</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-desc">Description</Label>
              <Textarea
                id="project-desc"
                value={projectForm.description}
                onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                placeholder="Project description"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="project-location">Location</Label>
              <Input
                id="project-location"
                value={projectForm.location}
                onChange={(e) => setProjectForm({...projectForm, location: e.target.value})}
                placeholder="Project location"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={projectForm.startDate}
                  onChange={(e) => setProjectForm({...projectForm, startDate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={projectForm.endDate}
                  onChange={(e) => setProjectForm({...projectForm, endDate: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Project Requirements</Label>
              <div className="space-y-2 pt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={projectForm.requirements?.dailyJsaRequired}
                    onChange={(e) => setProjectForm({
                      ...projectForm,
                      requirements: {...projectForm.requirements, dailyJsaRequired: e.target.checked}
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Daily JSA Required</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={projectForm.requirements?.weeklyToolboxTalkRequired}
                    onChange={(e) => setProjectForm({
                      ...projectForm,
                      requirements: {...projectForm.requirements, weeklyToolboxTalkRequired: e.target.checked}
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Weekly Toolbox Talk Required</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={projectForm.requirements?.preTaskPlanRequired}
                    onChange={(e) => setProjectForm({
                      ...projectForm,
                      requirements: {...projectForm.requirements, preTaskPlanRequired: e.target.checked}
                    })}
                    className="rounded"
                  />
                  <span className="text-sm">Pre-Task Plan Required</span>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProjectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject}>
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>
              Update project details or reassign to a different Project GC.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-project-name">Project Name *</Label>
              <Input
                id="edit-project-name"
                value={projectForm.name}
                onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-project-gc">Project GC</Label>
              <Select
                value={projectForm.projectGcId}
                onValueChange={(value) => setProjectForm({...projectForm, projectGcId: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a Project GC" />
                </SelectTrigger>
                <SelectContent>
                  {availableProjectGcs.map((gc) => (
                    <SelectItem key={gc.id} value={gc.id}>
                      {gc.name} {gc.companyCode ? `(${gc.companyCode})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-project-desc">Description</Label>
              <Textarea
                id="edit-project-desc"
                value={projectForm.description}
                onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-project-location">Location</Label>
              <Input
                id="edit-project-location"
                value={projectForm.location}
                onChange={(e) => setProjectForm({...projectForm, location: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-project-status">Status</Label>
              <Select
                value={projectForm.status || 'active'}
                onValueChange={(value) => setProjectForm({...projectForm, status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-start-date">Start Date</Label>
                <Input
                  id="edit-start-date"
                  type="date"
                  value={projectForm.startDate}
                  onChange={(e) => setProjectForm({...projectForm, startDate: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end-date">End Date</Label>
                <Input
                  id="edit-end-date"
                  type="date"
                  value={projectForm.endDate}
                  onChange={(e) => setProjectForm({...projectForm, endDate: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProjectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProject}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={showProjectDetailsDialog} onOpenChange={setShowProjectDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" />
              {selectedProjectDetails?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProjectDetails && (
            <div className="space-y-6 py-4">
              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Project GC</p>
                  <p className="font-medium">{selectedProjectDetails.gcCompanyName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getStatusColor(selectedProjectDetails.status)}>
                    {selectedProjectDetails.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{selectedProjectDetails.location || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">
                    {selectedProjectDetails.createdAt ? new Date(selectedProjectDetails.createdAt).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
              
              {/* Description */}
              {selectedProjectDetails.description && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="text-gray-700">{selectedProjectDetails.description}</p>
                </div>
              )}
              
              {/* Requirements */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Project Requirements</p>
                <div className="flex flex-wrap gap-2">
                  {selectedProjectDetails.requirements?.dailyJsaRequired && (
                    <Badge variant="outline" className="bg-blue-50">Daily JSA</Badge>
                  )}
                  {selectedProjectDetails.requirements?.weeklyToolboxTalkRequired && (
                    <Badge variant="outline" className="bg-green-50">Weekly Toolbox Talk</Badge>
                  )}
                  {selectedProjectDetails.requirements?.preTaskPlanRequired && (
                    <Badge variant="outline" className="bg-orange-50">Pre-Task Plan</Badge>
                  )}
                  <Badge variant="outline" className="bg-purple-50">
                    Audit: {selectedProjectDetails.requirements?.auditFrequency || 'monthly'}
                  </Badge>
                </div>
              </div>
              
              {/* Subcontractors */}
              {selectedProjectDetails.subcontractors?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Subcontractors ({selectedProjectDetails.subcontractors.length})</p>
                  <div className="space-y-2">
                    {selectedProjectDetails.subcontractors.map((sub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span>{sub.name || sub.companyName}</span>
                        <Badge variant="outline">{sub.status || 'active'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Safety Stats */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Safety Statistics (Last 30 Days)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-red-50 rounded">
                    <p className="text-2xl font-bold text-red-600">{selectedProjectDetails.recentIncidents || 0}</p>
                    <p className="text-xs text-gray-600">Incidents</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-2xl font-bold text-blue-600">{selectedProjectDetails.recentAudits || 0}</p>
                    <p className="text-xs text-gray-600">Audits</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded">
                    <p className="text-2xl font-bold text-green-600">{selectedProjectDetails.subcontractorCount || 0}</p>
                    <p className="text-xs text-gray-600">Subs</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDetailsDialog(false)}>
              Close
            </Button>
            <Button onClick={() => { setShowProjectDetailsDialog(false); openEditProjectDialog(selectedProjectDetails); }}>
              Edit Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentGCDashboard;
